import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Settings2, ChevronLeft, ChevronRight, RotateCcw, Loader2, Undo2, Download, Upload, AlertTriangle, X, CheckCircle2, Search, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getFriendlyError } from '../utils/friendlyErrors';
import BulkEditorGrid from '../components/BulkEditorGrid';

const ALL_SENTINEL = '__all__';

const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Add Date (Newest First)' },
  { value: 'updated_desc', label: 'Edit Date (Recently Updated)' },
  { value: 'name_asc', label: 'Product Name (A-Z)' },
  { value: 'vendor_asc', label: 'Vendor Name (A-Z)' },
  { value: 'vendor_location_asc', label: 'Vendor Location (A-Z)' },
  { value: 'price_desc', label: 'Price (High to Low)' },
  { value: 'price_asc', label: 'Price (Low to High)' },
];

// iter-356 — Full CSV/spreadsheet-style Bulk Product Editor. Every product
// is a row, every product-form field is a column — the expanded successor
// to the old bulk-update dialog (category/subcategory/vendor/tags only).

const ALL_COLUMNS = [
  { key: 'image', label: '', type: 'image', width: 56, group: 'default' },
  { key: 'title', label: 'Product Title', type: 'text', width: 260, group: 'default' },
  { key: 'status', label: 'Status', type: 'select', options: ['draft', 'active', 'suspended', 'pending_approval'], width: 140, group: 'default' },
  { key: 'category', label: 'Category', type: 'readonly', width: 130, group: 'default' },
  { key: 'subcategory', label: 'Sub-Category', type: 'readonly', width: 130, group: 'default' },
  { key: 'vendor_name', label: 'Vendor', type: 'readonly', width: 150, group: 'default', adminOnly: true },
  { key: 'vendor_location', label: 'Vendor Location', type: 'readonly', width: 150, group: 'default', adminOnly: true },
  { key: 'price', label: 'Price (₹)', type: 'number', width: 110, group: 'default' },
  { key: 'comparePrice', label: 'Compare Price (₹)', type: 'number', width: 140, group: 'default' },
  { key: 'inventory', label: 'Inventory', type: 'number', width: 100, group: 'default' },
  { key: 'sku', label: 'SKU', type: 'text', width: 120, group: 'default' },
  { key: 'country_of_origin', label: 'Country of Origin', type: 'text', width: 160, group: 'compliance' },
  { key: 'buying_mode', label: 'Buying Mode', type: 'select', options: [{ value: 'instant', label: 'Cart / Buy Now' }, { value: 'enquiry', label: 'Enquiry Only' }], width: 150, group: 'compliance' },
  { key: 'license_type', label: 'License Type', type: 'select', options: [], width: 130, group: 'compliance' },
  { key: 'license_number', label: 'License Number', type: 'text', width: 170, group: 'compliance' },
  { key: 'license_status', label: 'FSSAI Verification', type: 'readonly', width: 190, group: 'compliance' },
  { key: 'weight', label: 'Weight (kg)', type: 'number', width: 110, group: 'more' },
  { key: 'brand', label: 'Brand', type: 'text', width: 130, group: 'more' },
  { key: 'tags', label: 'Tags', type: 'text', width: 200, group: 'more' },
  { key: 'net_quantity_value', label: 'Net Qty Value', type: 'number', width: 120, group: 'more' },
  { key: 'net_quantity_unit', label: 'Net Qty Unit', type: 'select', options: ['g', 'kg', 'ml', 'L', 'pieces'], width: 110, group: 'more' },
  { key: 'shelf_life_days', label: 'Shelf Life (days)', type: 'number', width: 140, group: 'more' },
  { key: 'min_freshness_guarantee_days', label: 'Min Freshness (days)', type: 'number', width: 150, group: 'more' },
  { key: 'seo_title', label: 'SEO Title', type: 'text', width: 180, group: 'more' },
  { key: 'seo_description', label: 'SEO Description', type: 'text', width: 220, group: 'more' },
];
const DEFAULT_VISIBLE = ['image', 'title', 'status', 'category', 'subcategory', 'vendor_name', 'price', 'comparePrice', 'inventory', 'sku', 'country_of_origin', 'buying_mode', 'license_type', 'license_number'];

const VARIANT_TO_BACKEND_FIELD = { sku: 'sku', price: 'price', inventory: 'inventory', status: 'is_available' };

// iter-357 — tiny CSV helpers for the round-trip export/import (no library
// needed for a few hundred rows / ~20 columns).
const escapeCsvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const pushField = () => { row.push(field); field = ''; };
  const pushRow = () => { pushField(); rows.push(row); row = []; };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') pushField();
    else if (c === '\n') { if (text[i - 1] !== '\r' || field !== '') pushRow(); }
    else if (c === '\r') { /* skip, handled by \n */ }
    else field += c;
  }
  if (field !== '' || row.length) pushRow();
  return rows.filter(r => r.length > 1 || r[0] !== '');
};

const coerceForImport = (raw, column) => {
  if (raw === undefined || raw === '') return column.type === 'number' ? null : (column.type === 'text' ? '' : undefined);
  if (column.type === 'number') { const n = parseFloat(raw); return Number.isNaN(n) ? null : n; }
  if (column.type === 'boolean') return ['true', 'yes', '1', 'y'].includes(String(raw).trim().toLowerCase());
  if (column.type === 'select') {
    const opts = column.options || [];
    const match = opts.find(o => String(typeof o === 'string' ? o : o.value).toLowerCase() === String(raw).trim().toLowerCase());
    return match ? (typeof match === 'string' ? match : match.value) : undefined;
  }
  return raw;
};

// iter-358 — Small reusable filter widgets for the Filters popover.
const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <div>
    <label className="text-xs font-medium text-gray-500">{label}</label>
    <Select value={value || ALL_SENTINEL} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SENTINEL}>All</SelectItem>
        {(options || []).map(o => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  </div>
);

const FilterRange = ({ label, minKey, maxKey, filters, updateFilter, testId }) => (
  <div>
    <label className="text-xs font-medium text-gray-500">{label}</label>
    <div className="flex items-center gap-2">
      <Input type="number" placeholder="Min" value={filters[minKey] ?? ''} onChange={(e) => updateFilter(minKey, e.target.value)} data-testid={`${testId}-min`} />
      <span className="text-gray-400 text-xs">to</span>
      <Input type="number" placeholder="Max" value={filters[maxKey] ?? ''} onChange={(e) => updateFilter(maxKey, e.target.value)} data-testid={`${testId}-max`} />
    </div>
  </div>
);

const BulkProductEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiCall, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const { toast } = useToast();

  const ids = searchParams.get('ids');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 100, license_types: [], categories_meta: {} });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(new Set(DEFAULT_VISIBLE));
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [changes, setChanges] = useState(new Map()); // id::field -> {id, field, value}
  const [variantChanges, setVariantChanges] = useState(new Map()); // id::combo::field -> {...}
  const [saveResult, setSaveResult] = useState(null); // {updated, variantsUpdated, skippedTitles, undoPayload}
  const [undoing, setUndoing] = useState(false);
  const importInputRef = useRef(null);

  // iter-358 — Search, Sort & Filters for the Bulk Product Editor.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({ categories: [], subcategories: [], countries_of_origin: [], brands: [], tags: [], vendors: [] });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    if (ids) return;
    apiCall('/api/listings/bulk-editor/filter-options').then(setFilterOptions).catch(() => {});
  }, [ids, apiCall]);

  const updateFilter = (key, value) => setFilters(prev => {
    const next = { ...prev };
    if (value === '' || value === undefined || value === ALL_SENTINEL) delete next[key];
    else next[key] = value;
    return next;
  });
  const clearFilters = () => { setFilters({}); setSearch(''); };
  const activeFilterCount = Object.keys(filters).length;

  const fetchRows = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (ids) {
        params.set('ids', ids);
      } else {
        params.set('page', targetPage);
        params.set('page_size', '100');
        params.set('sort', sortBy);
        if (debouncedSearch) params.set('search', debouncedSearch);
        Object.entries(debouncedFilters).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') params.set(k, v);
        });
      }
      const res = await apiCall(`/api/listings/bulk-editor/rows?${params.toString()}`);
      setRows(res.rows || []);
      setMeta(res);
      setPage(res.page || 1);
    } catch (e) {
      toast({ title: 'Failed to load products', description: getFriendlyError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [ids, apiCall, toast, sortBy, debouncedSearch, debouncedFilters]);

  useEffect(() => { fetchRows(1); }, [fetchRows]);

  const columns = useMemo(() => {
    return ALL_COLUMNS
      .filter(c => visibleColumns.has(c.key))
      .filter(c => !c.adminOnly || isAdmin)
      .map(c => c.key === 'license_type' ? { ...c, options: meta.license_types || [] } : c);
  }, [visibleColumns, isAdmin, meta.license_types]);

  const displayRows = useMemo(() => {
    const out = [];
    for (const raw of rows) {
      const effective = { ...raw };
      for (const [, ch] of changes) {
        if (ch.id === raw.id) effective[ch.field] = ch.value;
      }
      out.push({
        rowKey: `product:${raw.id}`,
        kind: 'product',
        productId: raw.id,
        category: raw.category,
        hasVariants: raw.has_variants,
        isExpanded: expandedIds.has(raw.id),
        values: effective,
      });
      if (raw.has_variants && expandedIds.has(raw.id)) {
        for (const v of raw.variants) {
          const vEffective = { title: v.options_label, sku: v.sku, price: v.price, inventory: v.inventory, status: v.is_available };
          for (const [, ch] of variantChanges) {
            if (ch.id === raw.id && ch.combinationKey === v.combination_key) vEffective[ch.field] = ch.value;
          }
          out.push({
            rowKey: `variant:${raw.id}:${v.combination_key}`,
            kind: 'variant',
            productId: raw.id,
            combinationKey: v.combination_key,
            category: raw.category,
            hasVariants: false,
            values: vEffective,
          });
        }
      }
    }
    return out;
  }, [rows, changes, variantChanges, expandedIds]);

  const dirtyKeys = useMemo(() => {
    const s = new Set();
    for (const [, ch] of changes) s.add(`product:${ch.id}::${ch.field}`);
    for (const [, ch] of variantChanges) s.add(`variant:${ch.id}:${ch.combinationKey}::${ch.field}`);
    return s;
  }, [changes, variantChanges]);

  const handleCellsChange = useCallback((edits) => {
    setChanges(prev => {
      const next = new Map(prev);
      for (const e of edits) {
        if (e.kind !== 'variant') {
          const field = e.colKey === 'image' ? 'primary_image_index' : e.colKey;
          next.set(`${e.id}::${field}`, { id: e.id, field, value: e.value });
        }
      }
      return next;
    });
    setVariantChanges(prev => {
      const next = new Map(prev);
      for (const e of edits) {
        if (e.kind === 'variant') {
          const field = VARIANT_TO_BACKEND_FIELD[e.colKey] || e.colKey;
          next.set(`${e.id}::${e.combinationKey}::${field}`, { id: e.id, combinationKey: e.combinationKey, field, value: e.value });
        }
      }
      return next;
    });
  }, []);

  const totalDirty = changes.size + variantChanges.size;

  const handleDiscard = () => { setChanges(new Map()); setVariantChanges(new Map()); };

  const handleSave = async () => {
    if (totalDirty === 0) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const changeList = Array.from(changes.values()).map(c => ({ id: c.id, field: c.field, value: c.value }));
      const variantChangeList = Array.from(variantChanges.values()).map(c => ({ id: c.id, combination_key: c.combinationKey, field: c.field, value: c.value }));
      const payload = { changes: changeList, variant_changes: variantChangeList };
      const res = await apiCall('/api/listings/bulk-editor/save', { method: 'PUT', body: JSON.stringify(payload) });

      const skippedSet = new Set(res.skipped_ids || []);
      const skippedTitles = Array.from(skippedSet).map(id => rows.find(r => r.id === id)?.title || id);

      // iter-357 — one-level "Undo Last Save": snapshot the OLD values
      // (read from the still-un-refetched `rows` state) for every change
      // that actually applied, so a single click can write them straight
      // back — skipped (not-owned) ids are excluded since nothing there
      // was actually written.
      const undoChanges = changeList
        .filter(c => !skippedSet.has(c.id))
        .map(c => ({ id: c.id, field: c.field, value: rows.find(r => r.id === c.id)?.[c.field] ?? (c.field === 'primary_image_index' ? 0 : null) }));
      const undoVariantChanges = variantChangeList
        .filter(c => !skippedSet.has(c.id))
        .map(c => {
          const oldVal = rows.find(r => r.id === c.id)?.variants?.find(v => v.combination_key === c.combination_key)?.[c.field];
          return { id: c.id, combination_key: c.combination_key, field: c.field, value: oldVal ?? null };
        });

      // Keep skipped rows' edits visibly dirty (they weren't actually
      // saved) — only clear the ones that succeeded.
      setChanges(prev => { const next = new Map(prev); for (const [k, v] of prev) if (!skippedSet.has(v.id)) next.delete(k); return next; });
      setVariantChanges(prev => { const next = new Map(prev); for (const [k, v] of prev) if (!skippedSet.has(v.id)) next.delete(k); return next; });

      setSaveResult({
        updated: res.updated,
        variantsUpdated: res.variants_updated,
        skippedTitles,
        undoPayload: (undoChanges.length || undoVariantChanges.length) ? { changes: undoChanges, variant_changes: undoVariantChanges } : null,
      });
      fetchRows(page);
    } catch (e) {
      toast({ title: 'Save failed', description: getFriendlyError(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = async () => {
    if (!saveResult?.undoPayload) return;
    setUndoing(true);
    try {
      await apiCall('/api/listings/bulk-editor/save', { method: 'PUT', body: JSON.stringify(saveResult.undoPayload) });
      toast({ title: 'Reverted', description: 'Last bulk save was undone.' });
      setSaveResult(null);
      fetchRows(page);
    } catch (e) {
      toast({ title: 'Undo failed', description: getFriendlyError(e), variant: 'destructive' });
    } finally {
      setUndoing(false);
    }
  };

  const toggleColumn = (key) => setVisibleColumns(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleExpand = (productId) => setExpandedIds(prev => {
    const next = new Set(prev);
    next.has(productId) ? next.delete(productId) : next.add(productId);
    return next;
  });

  // iter-357 — CSV round-trip, scoped to whatever rows/columns are
  // currently loaded in this view (the same "select rows or open a page"
  // scope already used for editing) — export, tweak in Excel, re-import.
  const handleExportCsv = () => {
    const exportCols = ALL_COLUMNS.filter(c => visibleColumns.has(c.key) && c.key !== 'image');
    const header = ['Product ID', ...exportCols.map(c => c.label || c.key)];
    const lines = [header.map(escapeCsvCell).join(',')];
    for (const raw of rows) {
      const effective = { ...raw };
      for (const [, ch] of changes) if (ch.id === raw.id) effective[ch.field] = ch.value;
      const line = [raw.id, ...exportCols.map(c => effective[c.key])];
      lines.push(line.map(escapeCsvCell).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-product-editor-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCsvFile = async (file) => {
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length < 2) { toast({ title: 'Empty CSV', variant: 'destructive' }); return; }
      const [header, ...dataRows] = parsed;
      const idIdx = header.indexOf('Product ID');
      if (idIdx === -1) { toast({ title: 'Invalid CSV', description: 'Missing "Product ID" column — re-export from this page first.', variant: 'destructive' }); return; }
      const colByLabel = {};
      ALL_COLUMNS.forEach(c => { colByLabel[c.label || c.key] = c; });

      const edits = [];
      let matchedRows = 0;
      const unmatchedIds = [];
      for (const dataRow of dataRows) {
        const id = dataRow[idIdx];
        const row = rows.find(r => r.id === id);
        if (!row) { if (id) unmatchedIds.push(id); continue; }
        matchedRows++;
        header.forEach((h, i) => {
          if (i === idIdx) return;
          const col = colByLabel[h];
          if (!col || col.type === 'readonly' || col.key === 'image') return;
          const coerced = coerceForImport(dataRow[i], col);
          if (coerced === undefined) return;
          const current = row[col.key] ?? (col.type === 'number' ? null : '');
          if (String(coerced ?? '') !== String(current ?? '')) {
            edits.push({ rowKey: `product:${id}`, kind: 'product', id, colKey: col.key, value: coerced });
          }
        });
      }
      if (edits.length) handleCellsChange(edits);
      toast({
        title: `CSV loaded — ${edits.length} change(s) staged`,
        description: `${matchedRows} row(s) matched this view.${unmatchedIds.length ? ` ${unmatchedIds.length} row(s) not in this view were skipped.` : ''} Review the highlighted cells, then Save & Publish.`,
      });
    } catch (e) {
      toast({ title: 'Import failed', description: getFriendlyError(e), variant: 'destructive' });
    }
  };

  const totalPages = ids ? 1 : Math.max(1, Math.ceil((meta.total || 0) / (meta.page_size || 100)));

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="bulk-product-editor-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/listings')} data-testid="bulk-editor-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-semibold">Bulk Product Editor</h1>
              <p className="text-sm text-gray-500">
                {ids ? `Editing ${rows.length} selected product(s)` : `${meta.total || 0} products · page ${page} of ${totalPages}`} — click a cell, type to edit, or copy/paste across rows.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="bulk-editor-columns-btn">
                  <Settings2 className="h-4 w-4 mr-2" /> Columns
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 max-h-96 overflow-y-auto">
                {['default', 'compliance', 'more'].map(group => (
                  <div key={group} className="mb-2">
                    <div className="text-xs font-semibold uppercase text-gray-400 mb-1">
                      {group === 'default' ? 'Basic' : group === 'compliance' ? 'Compliance' : 'More Fields'}
                    </div>
                    {ALL_COLUMNS.filter(c => c.group === group && (!c.adminOnly || isAdmin)).map(col => (
                      <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 px-1 rounded text-sm">
                        <input type="checkbox" checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />
                        {col.label || 'Image'}
                      </label>
                    ))}
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <input
              type="file"
              accept=".csv"
              ref={importInputRef}
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportCsvFile(f); e.target.value = ''; }}
              data-testid="bulk-editor-import-input"
            />
            <Button variant="outline" size="sm" onClick={() => importInputRef.current?.click()} data-testid="bulk-editor-import-btn">
              <Upload className="h-4 w-4 mr-2" /> Import CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={rows.length === 0} data-testid="bulk-editor-export-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            {totalDirty > 0 && (
              <Button variant="outline" size="sm" onClick={handleDiscard} data-testid="bulk-editor-discard-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Discard ({totalDirty})
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={totalDirty === 0 || saving} data-testid="bulk-editor-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save & Publish {totalDirty > 0 ? `(${totalDirty})` : ''}
            </Button>
          </div>
        </div>

        {!ids && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-editor-toolbar">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, SKU or vendor…"
                className="pl-8"
                data-testid="bulk-editor-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="bulk-editor-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-56" data-testid="bulk-editor-sort-select">
                <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value} data-testid={`bulk-editor-sort-option-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="bulk-editor-filters-btn">
                  <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white text-xs" data-testid="bulk-editor-filters-count">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-96 max-h-[32rem] overflow-y-auto space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Filters</h4>
                  {(activeFilterCount > 0 || search) && (
                    <button onClick={clearFilters} className="text-xs text-blue-600 hover:underline" data-testid="bulk-editor-clear-filters">
                      Clear all
                    </button>
                  )}
                </div>

                <FilterSelect label="Category" value={filters.category} options={filterOptions.categories} onChange={(v) => updateFilter('category', v)} testId="bulk-editor-filter-category" />
                <FilterSelect label="Sub-Category" value={filters.subcategory} options={filterOptions.subcategories} onChange={(v) => updateFilter('subcategory', v)} testId="bulk-editor-filter-subcategory" />
                {isAdmin && filterOptions.vendors.length > 0 && (
                  <FilterSelect
                    label="Vendor"
                    value={filters.vendor_id}
                    options={filterOptions.vendors.map(v => ({ value: v.id, label: v.name }))}
                    onChange={(v) => updateFilter('vendor_id', v)}
                    testId="bulk-editor-filter-vendor"
                  />
                )}
                <FilterSelect label="Country of Origin" value={filters.country_of_origin} options={filterOptions.countries_of_origin} onChange={(v) => updateFilter('country_of_origin', v)} testId="bulk-editor-filter-country" />
                <FilterSelect label="Brand" value={filters.brand} options={filterOptions.brands} onChange={(v) => updateFilter('brand', v)} testId="bulk-editor-filter-brand" />

                <div>
                  <label className="text-xs font-medium text-gray-500">Tags</label>
                  <Input
                    placeholder="e.g. organic"
                    value={filters.tags || ''}
                    onChange={(e) => updateFilter('tags', e.target.value)}
                    data-testid="bulk-editor-filter-tags"
                  />
                </div>

                <FilterRange label="Price (₹)" minKey="price_min" maxKey="price_max" filters={filters} updateFilter={updateFilter} testId="bulk-editor-filter-price" />
                <FilterRange label="Inventory" minKey="inventory_min" maxKey="inventory_max" filters={filters} updateFilter={updateFilter} testId="bulk-editor-filter-inventory" />
                <FilterRange label="Weight (kg)" minKey="weight_min" maxKey="weight_max" filters={filters} updateFilter={updateFilter} testId="bulk-editor-filter-weight" />
                <FilterRange label="Shelf Life (days)" minKey="shelf_life_min" maxKey="shelf_life_max" filters={filters} updateFilter={updateFilter} testId="bulk-editor-filter-shelf-life" />
              </PopoverContent>
            </Popover>

            {(activeFilterCount > 0 || search) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="bulk-editor-clear-all-btn">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}

        {saveResult && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start justify-between gap-3" data-testid="bulk-editor-save-banner">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-800">
                <p className="font-medium">
                  Saved {saveResult.updated} product(s) + {saveResult.variantsUpdated} variant(s). Shopify sync queued.
                </p>
                {saveResult.skippedTitles.length > 0 && (
                  <p className="flex items-start gap-1.5 mt-1 text-amber-700" data-testid="bulk-editor-skipped-notice">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {saveResult.skippedTitles.length} product(s) were <strong>not saved</strong> — you don't have permission to edit: {saveResult.skippedTitles.join(', ')}. Their edits are still highlighted below.
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saveResult.undoPayload && (
                <Button variant="outline" size="sm" onClick={handleUndo} disabled={undoing} data-testid="bulk-editor-undo-btn">
                  {undoing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-2" />}
                  Undo Last Save
                </Button>
              )}
              <button onClick={() => setSaveResult(null)} className="text-emerald-600 hover:text-emerald-800" data-testid="bulk-editor-dismiss-banner">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading products…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="bulk-editor-empty-state">
            {activeFilterCount > 0 || search ? 'No products match your search/filters.' : 'No products found.'}
          </div>
        ) : (
          <BulkEditorGrid
            columns={columns}
            rows={displayRows}
            dirtyKeys={dirtyKeys}
            onExpandToggle={toggleExpand}
            onCellsChange={handleCellsChange}
            categoriesMeta={meta.categories_meta || {}}
          />
        )}

        {!ids && !loading && rows.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Showing {(page - 1) * meta.page_size + 1}–{(page - 1) * meta.page_size + rows.length} of {meta.total}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchRows(page - 1)} data-testid="bulk-editor-prev-page">
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchRows(page + 1)} data-testid="bulk-editor-next-page">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BulkProductEditor;
